import { useEffect, useMemo, useState } from 'react';

import type { LunaClient } from '@vlabs/api-bindings/src';
import { apiContainer } from '@vlabs/api-bindings/src';
import qs from 'qs';
import { useLocation, useParams } from 'react-router-dom';

import type { TFormType, TImageCheckFormSubmitValues } from './image-check-form/types';

export type TTaskDataState = 'notAsked' | 'loading' | 'failed' | 'fetched';

export interface UseTaskDataReturn {
  data?: TImageCheckFormSubmitValues;
  state: TTaskDataState;
}

export function useTaskData(): UseTaskDataReturn {
  const [data, setData] = useState<TImageCheckFormSubmitValues>();
  const [state, setState] = useState<TTaskDataState>('notAsked');
  const { objectId } = useParams<{
    objectId: string;
  }>();
  const { search } = useLocation();

  const lunaClient = (apiContainer.lunaClient as LunaClient);

  useEffect(() => {
    const generateTaskData = async () => {
      setState('loading');
      const parsed = qs.parse(search, {
        ignoreQueryPrefix: true,
      });
      const checks = {
        deepfake: parsed.deepfake === 'true',
        icao: parsed.icao === 'true',
        iso: parsed.iso === 'true',
        liveness: parsed.liveness === 'true',
        mindigital: parsed.mindigital === 'true',
      };
      const objectResp = await lunaClient.objects.get(objectId, {
        responseType: 'blob',
        params: {
          with_meta: 1,
        },
      });
      const filename = objectResp.headers['x-luna-meta-filename'] ?? parsed.filename;
      const fileList = new DataTransfer();
      fileList.items.add(new File(
        [(objectResp.data as Blob)],
        filename,
        { type: (objectResp.data as Blob).type },
      ));
      setState('fetched');
      setData({
        ...checks,
        filename,
        fileList: fileList.files,
        type: parsed?.type as TFormType | undefined ?? 'content',
      });
    };

    generateTaskData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, objectId]);

  return useMemo(() => ({
    data,
    state,
  }), [data, state]);
}
